import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Sidebar plan card — owners only.
 *
 * Renders the active subscription as its own card above the user-
 * profile card at the bottom of the sidebar (previously the plan was
 * a small chip crammed inside the account-meta block, which mixed
 * billing state with account identity in a single bordered card).
 *
 * Returns null when:
 *   - viewer is not an owner (teammates get the <PlanIndicator> dot
 *     inside account-meta — decision #8 keeps billing details hidden
 *     from them),
 *   - no active subscription (free trial, lapsed — the existing
 *     TrialBanner handles those states up top),
 *   - planTier isn't a tier we recognise.
 *
 * Surfaces the three states an active owner can be in:
 *   - normal renewing            → "Renews 8 Jul 2026"
 *   - cancel_at_period_end       → "Ends 8 Jul 2026" (amber accent)
 *   - pendingChange (downgrade
 *     or cadence change scheduled) → "Switching to Core on 8 Jul"
 */

type Tier = 'core' | 'premium' | 'enterprise';

/* Palette-only tier accents (CC4): teal for the paid tiers, amber for
   enterprise. The old indigo sat outside the landing palette. */
const TIER_COLOR: Record<Tier, string> = {
  core: 'var(--lanura-teal-500, #14b8a6)',
  premium: 'var(--lanura-teal-700, #0f766e)',
  enterprise: 'var(--lanura-warning-fill, #f59e0b)',
};

const TIER_NAME: Record<Tier, string> = {
  core: 'Core',
  premium: 'Premium',
  enterprise: 'Enterprise',
};

const PlanCard: React.FC<{ user: any }> = ({ user }) => {
  const navigate = useNavigate();
  if (!user || user.role !== 'owner') return null;

  // The workspace's plan, which for an owner who is not the payer lives on
  // someone else's record. Without this the badge vanished for exactly the
  // owner who most needed to know the workspace was still paid for. Who pays
  // is NOT shown here on purpose: this badge reads the same for every company,
  // and the Plan tab is where that detail lives.
  const sub = user.workspaceBilling?.subscription ?? user.subscription;
  const tier = sub?.planTier as Tier | undefined;
  if (!sub?.active || !tier || !TIER_COLOR[tier]) return null;

  const color = TIER_COLOR[tier];
  const name = sub.planName || TIER_NAME[tier];
  // A seat-exempt workspace is granted its plan, so "Annual billing" and a
  // renewal date would both be untrue. The backend sends it no dates.
  const cadenceLabel = sub.seatExempt
    ? null
    : sub.cadence === 'annual'
      ? 'Annual'
      : sub.cadence === 'monthly'
        ? 'Monthly'
        : null;
  const dateStr = (iso?: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : null;

  const cancelEnd = sub.cancelAtPeriodEnd ? dateStr(sub.currentPeriodEnd) : null;
  const pending = sub.pendingChange?.planTier ? sub.pendingChange : null;
  const pendingTargetName = pending
    ? TIER_NAME[pending.planTier as Tier] || pending.planTier
    : null;
  const pendingDate = pending ? dateStr(pending.scheduledFor) : null;
  const renewDate = !cancelEnd && !pending ? dateStr(sub.currentPeriodEnd) : null;

  return (
    <button
      type="button"
      className="plan-card"
      onClick={() => navigate('/subscription')}
      title="Open subscription"
      style={{
        // Flat row inside the sidebar account card (CC7): no border or fill
        // of its own; Layout.css draws the hairline under it.
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: 'transparent',
        borderRadius: 0,
        padding: '0 0 0 2px',
        marginBottom: 0,
        cursor: 'pointer',
        boxShadow: 'none',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: color,
            flexShrink: 0,
          }}
        />
        <strong
          style={{
            color: 'var(--lanura-text-primary, #0f172a)',
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Plan: {name}
        </strong>
      </div>

      {cadenceLabel && (
        <div
          style={{
            color: 'var(--lanura-text-secondary, #6b7280)',
            fontSize: 11,
            marginBottom: 2,
          }}
        >
          {cadenceLabel} billing
        </div>
      )}

      {pending ? (
        <div
          style={{
            color: 'var(--lanura-warning, #b45309)',
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Switching to {pendingTargetName}
          {pendingDate ? ` on ${pendingDate}` : ''}
        </div>
      ) : cancelEnd ? (
        <div
          style={{
            color: 'var(--lanura-warning, #b45309)',
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          Ends {cancelEnd}
        </div>
      ) : renewDate ? (
        <div
          style={{
            color: 'var(--lanura-text-secondary, #6b7280)',
            fontSize: 11,
          }}
        >
          Renews {renewDate}
        </div>
      ) : null}
    </button>
  );
};

export default PlanCard;
